import { InPlaceEditorProps, Labeled, SelectInput, useRecordContext, Validator } from "react-admin";
import { AegleInPlaceEditor } from "./AegleInPlaceEditor";
import { Typography } from "@mui/material";
import { ValidAny } from "@/types";

/** Gets nested value from object by dot-separated path */
const getByPath = (obj: ValidAny, path: string): ValidAny =>
  path.split(".").reduce((acc, k) => (acc === null || acc === undefined ? acc : acc[k]), obj);

interface InPlaceSelectEditorProps extends Omit<InPlaceEditorProps, "input" | "source"> {
  source: string;
  label?: string;
  choices?: Array<{ id: string | number; name: string }>;
  readOnly?: boolean;
  validate?: Validator | Validator[];
}

/**
 * In-place editor for select/dropdown fields.
 *
 * Renders a read view showing the selected choice's name and allows inline editing
 * using {@link SelectInput}.
 * Wrapped in a {@link Labeled} component to optionally display a label.
 *
 * @param source - The field name to bind to
 * @param label - Optional label for the field
 * @param choices - Array of choice objects with id and name properties
 * @param readOnly - If true, disables editing and shows display only
 * @param rest - Additional props passed to {@link InPlaceEditor} and {@link SelectInput}
 */
const InPlaceSelectEditor = ({
  source,
  label,
  choices,
  readOnly = false,
  validate,
  ...rest
}: InPlaceSelectEditorProps) => {
  const record = useRecordContext();
  const currentValue = getByPath(record, source);

  // Find the display name for the current value
  const displayValue = choices?.find((c) => c.id === currentValue)?.name || "-";

  // Read-only mode: just show the display component
  if (readOnly) {
    return (
      <Labeled label={label}>
        <Typography variant="body1" sx={{ display: "block", mb: 0.5 }}>
          {displayValue}
        </Typography>
      </Labeled>
    );
  }

  return (
    <Labeled label={label}>
      <AegleInPlaceEditor
        source={source}
        editor={
          <SelectInput
            {...rest}
            source={source}
            choices={choices}
            variant="standard"
            size="small"
            margin="none"
            label={false}
            helperText={validate ? undefined : false}
            validate={validate}
            autoFocus
            sx={{
              "& .MuiInput-root": { marginTop: 0 },
            }}
          />
        }
        {...rest}
      >
        <Typography variant="body1" sx={{ display: "block", mb: 0.5 }}>
          {displayValue}
        </Typography>
      </AegleInPlaceEditor>
    </Labeled>
  );
};

export default InPlaceSelectEditor;
